import { RuleApplyDataFields } from './RuleApplyDataFields';
import { RuleMessageFields } from './RuleMessageFields';
import {
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  useComputedColorScheme,
} from '@mantine/core';
import { RuleMode } from '@billing/validation/rules';
import type { ReactElement } from 'react';
import type { RuleBuilder } from './useRuleBuilder';
export const RuleOutcomeFields = ({
  builder,
}: {
  builder: RuleBuilder;
}): ReactElement => {
  const colorScheme = useComputedColorScheme();
  const background = colorScheme === 'dark' ? 'dark.6' : 'white';
  const {
    ruleMode,
    translate,
    transformAppendFlags,
    setTransformAppendFlags,
    transformCollapseByCode,
    setTransformCollapseByCode,
    transformApplyPercentages,
    setTransformApplyPercentages,
    timeCodeAfterMidnight,
    setTimeCodeAfterMidnight,
    timeCodeEvening,
    setTimeCodeEvening,
    timeCodeWeekend,
    setTimeCodeWeekend,
    transformPriority,
    setTransformPriority,
    flagLabel,
    setFlagLabel,
    flagMessage,
    setFlagMessage,
    flagSeverity,
    setFlagSeverity,
  } = builder;
  if (ruleMode === RuleMode.ApplyData)
    return <RuleApplyDataFields builder={builder} />;
  if (ruleMode === RuleMode.TariffTransforms) {
    return (
      <Paper withBorder radius="md" p="md" bg={background}>
        <Stack gap="xs">
          <Switch
            checked={transformAppendFlags}
            onChange={(event) =>
              setTransformAppendFlags(event.currentTarget.checked)
            }
            label={translate('pages:rules.editControls.appendTimeFlags')}
            data-testid="rule-transform-flags"
          />
          <Switch
            checked={transformCollapseByCode}
            onChange={(event) =>
              setTransformCollapseByCode(event.currentTarget.checked)
            }
            label={translate('pages:rules.editControls.collapseTariffs')}
            data-testid="rule-transform-collapse"
          />
          <Switch
            checked={transformApplyPercentages}
            onChange={(event) =>
              setTransformApplyPercentages(event.currentTarget.checked)
            }
            label={translate('pages:rules.editControls.applyPercentages')}
            data-testid="rule-transform-percentages"
          />
          <TextInput
            label={translate('pages:rules.editControls.afterMidnightCode')}
            value={timeCodeAfterMidnight}
            onChange={(event) =>
              setTimeCodeAfterMidnight(event.currentTarget.value)
            }
            data-testid="rule-transform-code-after-midnight"
          />
          <TextInput
            label={translate('pages:rules.editControls.eveningCode')}
            value={timeCodeEvening}
            onChange={(event) => setTimeCodeEvening(event.currentTarget.value)}
            data-testid="rule-transform-code-evening"
          />
          <TextInput
            label={translate('pages:rules.editControls.weekendCode')}
            value={timeCodeWeekend}
            onChange={(event) => setTimeCodeWeekend(event.currentTarget.value)}
            data-testid="rule-transform-code-weekend"
          />
          <TextInput
            label={translate('pages:rules.editControls.priority')}
            value={transformPriority}
            onChange={(event) =>
              setTransformPriority(event.currentTarget.value)
            }
            data-testid="rule-transform-priority"
          />
        </Stack>
      </Paper>
    );
  }
  if (ruleMode === 'flagOnly') {
    return (
      <Paper withBorder radius="md" p="md" bg={background}>
        <Stack gap="sm">
          <Stack gap={2}>
            <Text fw={600}>
              {translate('pages:rules.editControls.flagTitle')}
            </Text>
            <Text c="dimmed" size="sm">
              {translate('pages:rules.editControls.flagDescription')}
            </Text>
          </Stack>
          <TextInput
            label={translate('pages:rules.editControls.flagLabel')}
            placeholder={translate('pages:rules.editControls.flagPlaceholder')}
            value={flagLabel}
            onChange={(event) => setFlagLabel(event.currentTarget.value)}
            data-testid="rule-flag-label"
          />
          <Textarea
            label={translate('pages:rules.editControls.flagMessage')}
            value={flagMessage}
            onChange={(event) => setFlagMessage(event.currentTarget.value)}
            minRows={2}
            data-testid="rule-flag-message"
          />
          <Select
            label={translate('pages:rules.editControls.flagSeverity')}
            data={['Low', 'Medium', 'High'].map((value) => ({
              value,
              label: translate(
                `pages:rules.editControls.${value.toLowerCase()}`
              ),
            }))}
            value={flagSeverity}
            onChange={(value) => setFlagSeverity(value || 'Medium')}
            allowDeselect={false}
            data-testid="rule-flag-severity"
          />
        </Stack>
      </Paper>
    );
  }
  return <RuleMessageFields builder={builder} />;
};
